import { useMemo, useState } from 'react';
import PageTemplate from './PageTemplate';
import {
  downloadPaymentsCsv,
  downloadPaymentsExcel,
  downloadPaymentsPdf,
} from '../utils/paymentExport';
import './ModulePage.css';

const initialForm = {
  transactionType: '',
  paymentStatus: '',
  startDate: '',
  endDate: '',
};

function Payments() {
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [payments, setPayments] = useState([]);
  const [exportFormat, setExportFormat] = useState('');

  const filteredPayments = useMemo(() => {
    const term = search.toLowerCase().trim();
    return payments.filter((payment) => {
      if (!term) return true;
      return (
        payment.transactionType.toLowerCase().includes(term) ||
        payment.paymentStatus.toLowerCase().includes(term)
      );
    });
  }, [payments, search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleExportFormatChange = (e) => {
    const v = e.target.value;
    if (!v) return;
    if (v === 'pdf') downloadPaymentsPdf(filteredPayments);
    else if (v === 'excel') downloadPaymentsExcel(filteredPayments);
    else if (v === 'csv') downloadPaymentsCsv(filteredPayments);
    setExportFormat('');
  };

  const handleAddPayment = (e) => {
    e.preventDefault();
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      return;
    }
    const nextPayment = {
      id: Date.now(),
      ...form,
    };
    setPayments((prev) => [nextPayment, ...prev]);
    setForm(initialForm);
    setShowForm(false);
  };

  return (
    <PageTemplate title="Payments" subtitle="Manage payments and transactions">
      <div className="teachers-page">
        <div className="module-actions">
          <input
            type="search"
            placeholder="Search by transaction type or payment status..."
            className="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="export-actions">
            <select
              id="payment-export"
              className="select-input export-select"
              value={exportFormat}
              onChange={handleExportFormatChange}
              aria-label="Download: choose PDF, Excel, or CSV"
            >
              <option value="">Download</option>
              <option value="pdf">PDF</option>
              <option value="excel">Excel</option>
              <option value="csv">CSV</option>
            </select>
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={() => setShowForm((prev) => !prev)}
          >
            {showForm ? 'Close Payment Form' : 'Add Payment'}
          </button>
        </div>

        {showForm ? (
          <div className="add-teacher-panel">
            <div className="add-teacher-header">
              <div>
                <h2>Add Payment</h2>
                <p>Enter transaction details using the same dashboard form style.</p>
              </div>
              <span className="add-teacher-badge">Payment Entry</span>
            </div>

            <form onSubmit={handleAddPayment} className="add-teacher-grid">
              <select
                name="transactionType"
                className="select-input"
                value={form.transactionType}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Transaction Method
                </option>
                <option value="UPI">UPI</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Debit Card">Debit Card</option>
                <option value="Netbanking">Netbanking</option>
              </select>
              <select
                name="paymentStatus"
                className="select-input"
                value={form.paymentStatus}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Payment Status
                </option>
                <option value="Success">Success</option>
                <option value="Pending">Pending</option>
                <option value="Refund">Refund</option>
              </select>

              <div className="form-group">
                <label htmlFor="startDate">Start Date</label>
                <input
                  id="startDate"
                  name="startDate"
                  type="date"
                  className="search-input"
                  value={form.startDate}
                  onChange={handleChange}
                  max={form.endDate || undefined}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="endDate">End Date</label>
                <input
                  id="endDate"
                  name="endDate"
                  type="date"
                  className="search-input"
                  value={form.endDate}
                  onChange={handleChange}
                  min={form.startDate || undefined}
                  required
                />
              </div>

              <div className="add-teacher-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowForm(false);
                    setForm(initialForm);
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Submit
                </button>
              </div>
            </form>
          </div>
        ) : null}

        {!showForm ? (
          <div className="teachers-table-wrapper" role="region" aria-label="Payments table">
            <table className="teachers-table">
              <thead>
                <tr>
                  <th>Transaction Method</th>
                  <th className="table-th-center">Payment Status</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="teachers-empty">
                      No payments added yet.
                    </td>
                  </tr>
                ) : (
                  filteredPayments.map((payment) => (
                    <tr key={payment.id}>
                      <td className="table-cell-strong">{payment.transactionType}</td>
                      <td className="table-td-center">
                        <span
                          className={`batch-status-pill ${
                            payment.paymentStatus === 'Success'
                              ? 'batch-status-active'
                              : payment.paymentStatus === 'Pending'
                              ? 'batch-status-upcoming'
                              : 'batch-status-completed'
                          }`}
                        >
                          {payment.paymentStatus}
                        </span>
                      </td>
                      <td>{payment.startDate || '—'}</td>
                      <td>{payment.endDate || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </PageTemplate>
  );
}

export default Payments;
